// @ts-strict-ignore
import Button from 'antd/lib/button';
import List from 'antd/lib/list';
import Space from 'antd/lib/space';
import Tag from 'antd/lib/tag';
import Collapse from 'antd/lib/collapse';
import Text from 'antd/lib/typography/Text';
import React from 'react';
import { AppComponentProps, SetupStep, SetupStepType } from '../../../typescriptHelpers';
import { AutomationTip, HelpTip, setupModuleStyle, InfoTip } from '../shared';
import Icon, { BellOutlined, SettingOutlined, CaretRightOutlined } from '@ant-design/icons';
import {
    selectJoinedCommsProvidersNamesList,
    selectNotifications,
    selectUserIsAdmin,
    selectWasAppCreatedAfterQualitativeOnboarding,
} from '../../../selectors/selectors';
import { useSelector } from 'react-redux';
import { RootState } from '../../../reducers/reducers';
import { getSetupStepLocalData } from '../../../helpers/localStorageHelper';
import { displayTextForCategory, iconForAppNotificationId } from '../../../helpers/displayHelpers';
import { antGrey, runwayColors, tagStyle } from '../../styles/globalStyles';
import { groupBy } from 'lodash';
import { categoryForAppNotification } from '../../../helpers';
import { AppNotificationCategory, AppNotification, IntegrationCapability } from '../../../types';
import NotificationSettingsModal from '../../appSettings/NotificationSettingsModal';

const SetupViewComponent: React.FC<AppComponentProps> = (props: AppComponentProps) => {
    const notifications = useSelector((state: RootState) => selectNotifications(state, props.appId));
    const appNotificationsByCategory = groupBy(notifications, (notification) =>
        categoryForAppNotification(notification.id),
    );
    const categorySortOrder = [
        AppNotificationCategory.ReleaseCycle,
        AppNotificationCategory.AppReviews,
        AppNotificationCategory.ReleaseAnnouncement,
        AppNotificationCategory.ChecklistItems,
        AppNotificationCategory.Testing,
        AppNotificationCategory.Builds,
        AppNotificationCategory.ReleaseProcessTasks,
    ];
    return (
        <div
            style={{
                ...setupModuleStyle,
                padding: 0,
                paddingTop: 8,
                paddingBottom: 8,
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            {categorySortOrder.map((category, index) => (
                <CollapsibleAppNotificationsList
                    key={category}
                    category={category as AppNotificationCategory}
                    notifications={appNotificationsByCategory[category]}
                    {...props}
                    showOpen={index === 0}
                />
            ))}
        </div>
    );
};

interface CollapsibleAppNotificationsListProps extends AppComponentProps {
    category: AppNotificationCategory;
    notifications: AppNotification[];
    showOpen: boolean;
}

const CollapsibleAppNotificationsList: React.FC<CollapsibleAppNotificationsListProps> = (
    props: CollapsibleAppNotificationsListProps,
) => {
    const [showNotificationSettings, setShowNotificationSettings] = React.useState(false);
    const [notificationIdSelectedForSettings, setNotificationIdSelectedForSettings] = React.useState(null);
    return (
        <Collapse
            bordered={false}
            expandIcon={({ isActive }) => (
                <CaretRightOutlined
                    style={{
                        color: antGrey.grey6,
                        fontSize: 22,
                    }}
                    rotate={isActive ? 90 : 0}
                />
            )}
            defaultActiveKey={props.showOpen ? props.category : null}
        >
            <Collapse.Panel
                key={props.category}
                style={{ padding: 10 }}
                header={
                    <Space size={12}>
                        <Text style={{ color: antGrey.grey8, fontSize: 14, fontWeight: 600 }}>
                            {displayTextForCategory(props.category)}
                        </Text>
                    </Space>
                }
            >
                <List
                    header={null}
                    pagination={false}
                    bordered={false}
                    dataSource={props.notifications}
                    renderItem={(notification) => {
                        return (
                            <List.Item key={notification.id} style={{ borderBottomWidth: 0, padding: 6 }}>
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        flexGrow: 1,
                                        backgroundColor: runwayColors.white,
                                        padding: 10,
                                        borderRadius: '8px',
                                    }}
                                >
                                    <Space size={18} align="center">
                                        <Icon
                                            component={iconForAppNotificationId(notification.id)}
                                            style={{
                                                fontSize: 16,
                                                color: notification.enabled ? antGrey.grey7 : antGrey.grey5,
                                                paddingTop: 4,
                                            }}
                                        />
                                        <Text type={notification.enabled ? undefined : 'secondary'}>
                                            {notification.displayText}
                                        </Text>
                                    </Space>
                                    <Space>
                                        {notification.enabled ?
                                            <Tag
                                                color={runwayColors.white}
                                                style={{
                                                    ...tagStyle,
                                                    fontSize: 10,
                                                    borderColor: runwayColors.statusGreen,
                                                    color: runwayColors.statusGreen,
                                                }}
                                            >
                                                Enabled
                                            </Tag>
                                        :   <Tag
                                                color={runwayColors.white}
                                                style={{
                                                    ...tagStyle,
                                                    fontSize: 10,
                                                    borderColor: antGrey.grey6,
                                                    color: antGrey.grey6,
                                                }}
                                            >
                                                Disabled
                                            </Tag>
                                        }
                                        <Button
                                            ghost
                                            icon={<SettingOutlined style={{ color: runwayColors.info }} />}
                                            onClick={() => {
                                                setNotificationIdSelectedForSettings(notification.id);
                                                setShowNotificationSettings(true);
                                            }}
                                        />
                                    </Space>
                                </div>
                            </List.Item>
                        );
                    }}
                ></List>
            </Collapse.Panel>
            {notificationIdSelectedForSettings && (
                <NotificationSettingsModal
                    appId={props.appId}
                    orgId={props.orgId}
                    onClose={() => setShowNotificationSettings(false)}
                    open={showNotificationSettings}
                    notificationId={notificationIdSelectedForSettings}
                    onAfterClose={() => {
                        setNotificationIdSelectedForSettings(null);
                    }}
                />
            )}
        </Collapse>
    );
};

const InfoViewComponent: React.FC<AppComponentProps> = () => {
    const commsProviders = useSelector(selectJoinedCommsProvidersNamesList);

    return (
        <Space direction="vertical">
            <InfoTip>
                All notifications will go to the primary channel you define when connecting {commsProviders}. You can
                also set up overrides for individual notifications if you want them sent to different channels.
            </InfoTip>
            <AutomationTip>
                Runway can automatically create release-specific channels when they’re relevant (and delete them when
                they’re not).
            </AutomationTip>
            <HelpTip>
                Questions, or want a walkthrough?{' '}
                <Button type="link" style={{ padding: 0, display: 'inline', marginLeft: 5 }} href={`#hs-chat-open`}>
                    Let us know!
                </Button>
            </HelpTip>
        </Space>
    );
};

export const AppNotificationsStep: SetupStep = {
    title: "Choose your team's notifications.",
    description: (allComms) => {
        return `During each release, Runway can send helpful ${allComms} messages to communicate progress and blockers, ping team members to remind them about tasks, and much more.`;
    },
    icon: () => BellOutlined,
    SetupViewComponent,
    InfoViewComponent,
    listTitle: 'Notifications',
    listDescription: 'Keep your entire team in the loop.',
    integrationsRecommendationBannerData: {
        title: (allComms) => {
            return `Required: Connect ${allComms}`;
        },
        description: 'Get timely notifications about all aspects of your release, right where your team already works.',
        actionText: 'OK, let’s do it',
        type: SetupStepType.AppNotifications,
        integrationCapabilities: [IntegrationCapability.Notifications],
    },
    isCompleteSelector: (root, { appId }) => {
        const isUserAdmin = selectUserIsAdmin(root);
        const wasAppCreatedAfterQualitativeOnboarding = selectWasAppCreatedAfterQualitativeOnboarding(root, appId);
        const wasVisited = getSetupStepLocalData(appId, SetupStepType.Automations)?.hasBeenVisited;
        return wasAppCreatedAfterQualitativeOnboarding && isUserAdmin ? wasVisited : true;
    },
};
